import ExportFileModal from 'components/modal/ExportFileModal'
import React, { useMemo, useCallback } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import ExportAction from 'export/actions/ExportAction'
import { formatData } from 'utils/ExportDataUtil'
import DtoOperation from 'station/components/suivipc/qualitometer/dto/DtoOperation'
import { groupBy, isNil, isUndefined, orderBy, round, uniqBy, keys, values, compact } from 'lodash'
import { calculateAverage, calculateGeometricAverage, calculateRangeInterquartile, calculateStandardDeviation, filterDetection, filterQuantification, filterResult, filterValid, searchMaxValue, searchMinValue, searchP90Value } from 'utils/AnalyseUtils'
import { getDate } from 'utils/DateUtil'
import ToastrAction from 'toastr/actions/ToastrAction'
import { usePiezoDatas } from '../hook/usePiezoDatas'
import { useHydroDatas } from '../hook/useHydroDatas'
import { usePluvioDatas } from '../hook/usePluvioDatas'
import useListIndexed from 'utils/customHook/useListIndexed'
import DtoQualityThresholds from 'quality/dto/QualityThreshold/DtoQualityThresholds'
import { getMarkerColor } from 'utils/QualityThresholdUtils'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'

const PcMonitoringExportModal = ({
    analysis = [],
    operations = [],
    threshold,

    hydroDatas = [],
    piezoDatas = [],
    pluvioDatas = [],

    groupEquivalences = false,

    exportModel = [],

    isExportModalOpen = false,
    closeExportModal = () => { },
}) => {
    const dispatch = useDispatch()

    const {
        parameters,
        parameterGroupUsage,
        units,
        fractions,
        supports,
        settings,
        thresholds,
        qualitometers,
        contributorsIndex,
        status,
        qualifications,
        points,
        associatedStationsPoints,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        parameterGroupUsage: store.ParameterReducer.parameterGroupUsage,
        units: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
        settings: store.AdministrationReducer.settings,
        thresholds: store.QualityReducer.thresholds,
        qualitometers: store.QualityReducer.qualitometersLight,
        contributorsIndex: store.ContributorReducer.contributorsIndex,
        status: store.QualityReducer.status,
        qualifications: store.QualityReducer.qualifications,
        points: store.QualityReducer.points,
        associatedStationsPoints: store.SuivipcReducer.associatedStationsPoints,
    }), shallowEqual)

    const parametersIndex = useListIndexed(parameters, 'code')
    const unitsIndex = useListIndexed(units, 'code')
    const fractionsIndex = useListIndexed(fractions, 'id')
    const supportsIndex = useListIndexed(supports, 'id')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const allPoints = useMemo(() => [...points, ...associatedStationsPoints], [associatedStationsPoints, points])

    const uniqOperations = useMemo(() => {
        return uniqBy(analysis, a => `${a.operation}:${a.qualitometer}`)
    }, [analysis])

    const operationsFound = useMemo(() => {
        return uniqOperations.map(a => operations.find(op => op.id === a.operation && op.qualitometer === a.qualitometer)).filter(o => !!o)
    }, [uniqOperations, operations])

    const operationsDatesSort = useMemo(() => {
        return orderBy(operationsFound, ['date', 'qualitometer'], ['desc', 'asc']).map(op => `${getDate(op.date)}#:#${op.qualitometer}#:#${op.id}`)
    }, [operationsFound])

    const shouldCalculateFlow = hydroDatas.some(({ calculateFlux }) => calculateFlux)

    const piezoDatasFormatted = usePiezoDatas(piezoDatas, operationsDatesSort)
    const hydroDatasFormatted = useHydroDatas(hydroDatas, operationsDatesSort)
    const pluvioDatasFormatted = usePluvioDatas(pluvioDatas, operationsDatesSort)

    const formatUnitLabel = unit => {
        if (isUndefined(unit)) {
            return ''
        }
        if (shouldCalculateFlow) {
            return `${unit.symbol} * m3/s`
        }
        return unit.symbol
    }

    const createKey = useCallback((op, givenAnalysis) => {
        const qualito = qualitometers.find(({ id }) => id === op.qualitometer)

        const producer = contributorsIndex[op.producer]
        const producerLabel = producer && (producer.mnemonique || producer.name) || ''

        const laboIds = uniqBy(givenAnalysis, 'labo').map(a => a.labo).filter(id => !isUndefined(id))
        const laboLabels = laboIds.map(id => {
            const labo = contributorsIndex[id]
            return labo && (labo.mnemonique || labo.name) || ''
        })

        const point = allPoints.find(p => p.idPoint === op.point && p.idQualitometer === op.qualitometer)

        return [
            `[${qualito?.code || ''}] ${qualito?.name || ''}`,
            `${point?.identifiant ? `[${point?.identifiant}] ` : ''}${point?.name || ''}`,
            `${i18n.date}: ${getDate(op.date)}`,
            `${i18n.status}: ${status.find(s => s.code === op.status)?.name ?? ''}`,
            `${i18n.qualification}: ${qualifications.find(q => q.code === op.qualification)?.name ?? ''}`,
            `${i18n.producer}: ${producerLabel}`,
            `${laboLabels.length > 1 ? i18n.laboratories : i18n.laboratory}: ${laboLabels.join(', ')}`,
        ].filter(e => e !== '')
    }, [allPoints, contributorsIndex, qualifications, qualitometers, status])

    const formatExportData = () => {
        const analysisGroupByParameter = groupBy(analysis, ({ parameter, unit, fraction, support }) => groupEquivalences ? parameter : `${parameter}#:#${unit}#:#${fraction}#:#${support}`)
        return keys(analysisGroupByParameter).map(paramKey => {
            const analysisGroup = analysisGroupByParameter[paramKey].map(analyse => {
                const linkedOperation = operationsFound.find(op => op.id === analyse.operation && op.qualitometer === analyse.qualitometer)
                const key = createKey(linkedOperation, [analyse]).join(' \n')
                const hydroValue = hydroDatasFormatted.reduce((res, { [key]: { shouldCalculate, resultWithFlux } }) => shouldCalculate ? res + resultWithFlux : res, 0)
                const value = shouldCalculateFlow ? `${round(parseFloat(analyse.value.replace('<', '').replace('>', '')) * hydroValue, 5)}` : `${analyse.value}`
                return {
                    ...analyse,
                    value,
                    result: shouldCalculateFlow ? analyse.result * hydroValue : analyse.result,
                }
            })

            const [parameterCode] = paramKey.split('#:#')
            const parameter = parametersIndex[parameterCode]

            const valuesObj = analysisGroup.reduce((acc, analyse) => {
                const linkedOperation = operationsFound.find(op => op.id === analyse.operation && op.qualitometer === analyse.qualitometer)
                const key = createKey(linkedOperation, [analyse]).join(' \n')
                acc[key] = {
                    value: analyse.value.replace('.', ','),
                    cellType: 'right',
                    color: isNil(analyse.index) && isNil(analyse.result) ? 'grey' : getMarkerColor(analyse.color),
                }
                return acc
            }, {})

            const unitCodes = uniqBy(analysisGroup, 'unit').map(a => a.unit).filter(u => !isUndefined(u))
            const fractionCodes = uniqBy(analysisGroup, 'fraction').map(a => a.fraction).filter(u => !isUndefined(u))
            const supportCodes = uniqBy(analysisGroup, 'support').map(a => a.support).filter(u => !isUndefined(u))
            const group = parameterGroupUsage.find(p => p.parameter === parameterCode)

            const validAnalysis = filterValid(filterResult(analysisGroup))

            const p90 = searchP90Value(validAnalysis).replace('.', ',')
            const min = searchMinValue(validAnalysis).replace('.', ',')
            const max = searchMaxValue(validAnalysis).replace('.', ',')
            const average = `${calculateAverage(validAnalysis, settings)}`.replace('.', ',')
            const geometricAverage = `${calculateGeometricAverage(validAnalysis)}`.replace('.', ',')
            const rangeInterquartile = `${calculateRangeInterquartile(validAnalysis)}`.replace('.', ',')
            const standardDeviation = `${calculateStandardDeviation(validAnalysis)}`.replace('.', ',')

            const nbAnalysis = validAnalysis.length
            const nbQuantification = filterQuantification(validAnalysis).length
            const nbDetection = filterDetection(validAnalysis).length
            const nbOverrun = validAnalysis.filter(a => a.index > 0).length

            const overrunTx = nbAnalysis ? `${round(nbOverrun * 100 / nbAnalysis, 2)} %`.replace('.', ',') : '0%'
            const quantTx = nbAnalysis ? `${round(nbQuantification * 100 / nbAnalysis, 3)} %`.replace('.', ',') : '0%'
            const detectTx = nbAnalysis ? `${round(nbDetection * 100 / nbAnalysis, 3)} %`.replace('.', ',') : '0%'

            return {
                group: group?.groupName,
                codeParameter: { value: parameterCode, cellType: 'right' },
                parameter: parameter?.name,
                codeUnit: { value: unitCodes.join(', '), cellType: 'right' },
                unit: unitCodes.map(code => formatUnitLabel(unitsIndex[code])).join(', '),
                supportCode: { value: supportCodes.join(', '), cellType: 'right' },
                support: supportCodes.map(code => supportsIndex[code]?.name).join(', '),
                fractionCode: { value: fractionCodes.join(', '), cellType: 'right' },
                fraction: fractionCodes.map(code => fractionsIndex[code]?.name).join(', '),
                ...valuesObj,
                min: { value: min, color: 'grey', cellType: 'right' },
                averageShort: { value: average, color: 'grey', cellType: 'right' },
                max: { value: max, color: 'grey', cellType: 'right' },
                percentile90: { value: p90, color: 'grey', cellType: 'right' },
                geometricAverageShort: { value: geometricAverage, color: 'grey', cellType: 'right' },
                rangeInterquartile: { value: rangeInterquartile, color: 'grey', cellType: 'right' },
                standardDeviation: { value: standardDeviation, color: 'grey', cellType: 'right' },
                overrunNb: { value: nbOverrun, color: 'grey', cellType: 'right' },
                overrunTx: { value: overrunTx, color: 'grey', cellType: 'right' },
                nbAnalysis: { value: nbAnalysis, color: 'grey', cellType: 'right' },
                nbQuant: { value: nbQuantification, color: 'grey', cellType: 'right' },
                nbDetect: { value: nbDetection, color: 'grey', cellType: 'right' },
                quantTx: { value: quantTx, color: 'grey', cellType: 'right' },
                detectTx: { value: detectTx, color: 'grey', cellType: 'right' },
                threshold1: { value: analysisGroup[0].threshold?.threshold1, format: '0.000', cellType: 'number' },
                threshold2: { value: analysisGroup[0].threshold?.threshold2, format: '0.000', cellType: 'number' },
                threshold3: { value: analysisGroup[0].threshold?.threshold3, format: '0.000', cellType: 'number' },
                threshold4: { value: analysisGroup[0].threshold?.threshold4, format: '0.000', cellType: 'number' },
            }
        })
    }

    const formatSubstances = exportDatas => {
        if (!threshold) {
            return []
        }
        const thresholdFound = thresholds.find(t => `${t.code}` === threshold.thresholdCode)
        const analysisValues = operationsDatesSort.reduce((acc, key) => {
            const keyData = exportDatas.map(d => d[key]).filter(d => d?.color && !['white', 'blue', 'grey'].includes(d.color))
            acc[key] = {
                value: keyData.length,
                cellType: 'right',
                color: keyData.length > 0 ? 'red' : 'grey',
            }
            return acc
        }, {})
        const operationsValues = operationsDatesSort.map(key => analysisValues[key].value)
        const overrunNb = operationsValues.filter(v => v !== 0).length

        const overrunTx = operationsValues.length ? `${round(overrunNb * 100 / operationsValues.length, 2)} %` : '0%'
        return [{
            parameter: { value: thresholdFound?.name, color: 'grey' },
            group: { value: '', color: 'grey' },
            unit: { value: '', color: 'grey' },
            support: { value: '', color: 'grey' },
            fraction: { value: '', color: 'grey' },
            ...analysisValues,
            overrunTx: {
                value: overrunTx,
                color: 'grey',
                cellType: 'right',
            },
            overrunNb: {
                value: overrunNb,
                color: 'grey',
                cellType: 'right',
            },
        }]
    }

    const operationHeaderTemplate = useMemo(() => {
        const groupAnalysis = groupBy(analysis, a => `${a.qualitometer}#${a.operation}`)
        const operationsOrdered = orderBy(operationsFound, ['date', 'qualitometer'], ['desc', 'asc'])
        return operationsOrdered.reduce((acc, op) => {
            const filteredAnalysis = groupAnalysis[`${op.qualitometer}#${op.id}`]
            acc[`${getDate(op.date)}#:#${op.qualitometer}#:#${op.id}`] = createKey(op, filteredAnalysis)
            return acc
        }, {})
    }, [analysis, operationsFound, createKey])

    const operationsHeader = values(operationHeaderTemplate)

    const headers = compact([
        'group',
        showSandreCode ? 'codeParameter' : '',
        'parameter',
        showSandreCode ? 'codeUnit' : '',
        'unit',
        showSandreCode ? 'supportCode' : '',
        'support',
        showSandreCode ? 'fractionCode' : '',
        'fraction',
        ...operationsHeader.map(o => o.join(' \n')),
        'min',
        'averageShort',
        'max',
        'percentile90',
        'geometricAverageShort',
        'nbDetect',
        'nbQuant',
        'nbAnalysis',
        'detectTx',
        'quantTx',
        'threshold1',
        'threshold2',
        'threshold3',
        'threshold4',
    ])

    const customHeaders = compact([
        'group',
        showSandreCode ? 'codeParameter' : '',
        'parameter',
        showSandreCode ? 'codeUnit' : '',
        'unit',
        showSandreCode ? 'supportCode' : '',
        'support',
        showSandreCode ? 'fractionCode' : '',
        'fraction',
        ...operationsHeader.map(o => o.join(' \n')),
        'min',
        'averageShort',
        'max',
        'percentile90',
        'geometricAverageShort',
        'nbDetect',
        'nbQuant',
        'nbAnalysis',
        'detectTx',
        'quantTx',
        'threshold1',
        'threshold2',
        'threshold3',
        'threshold4',
    ])

    const onExport = type => {
        const data = formatExportData()
        const dataOrdered = [
            ...formatSubstances(data),
            ...piezoDatasFormatted,
            ...pluvioDatasFormatted,
            ...hydroDatasFormatted,
            ...orderBy(data, 'codeParameter.value'),
        ]
        if (dataOrdered.length) {
            const exportData = [
                {
                    ...dataOrdered[0],
                    headers,
                    customHeaders,
                },
                ...dataOrdered.slice(1),
            ]
            dispatch(ExportAction.export(formatData(exportData), type, i18n.followUpPC))
        } else {
            dispatch(ToastrAction.warning(i18n.noDataToExport))
        }
    }

    return (
        <ExportFileModal
            open={isExportModalOpen}
            onClose={() => closeExportModal(false)}
            data={[
                {
                    name: i18n.resultsTable,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => onExport('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => onExport('csv'),
                    }],
                },
                ...exportModel,
            ]}
        />
    )
}

PcMonitoringExportModal.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.shape({/* ...DtoAnalysis, ...calculateThresholdsResult */ })),
    operations: PropTypes.arrayOf(PropTypes.instanceOf(DtoOperation)),
    threshold: PropTypes.instanceOf(DtoQualityThresholds),

    hydroDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.string,
        calculateFlux: PropTypes.bool,
        coeffFlux: PropTypes.number,
        measures: PropTypes.arrayOf(PropTypes.shape({})),
    })),
    piezoDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        measures: PropTypes.arrayOf(PropTypes.shape({})),
    })),
    pluvioDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.number,
        offset: PropTypes.number,
        cumul: PropTypes.number,
        measures: PropTypes.arrayOf(PropTypes.shape({})),
    })),

    groupEquivalences: PropTypes.bool,
    exportModel: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        formats: PropTypes.arrayOf(PropTypes.shape({
            type: PropTypes.string,
            callback: PropTypes.func,
        })),
    })),

    isExportModalOpen: PropTypes.bool,
    closeExportModal: PropTypes.func,
}

export default PcMonitoringExportModal